import { useEnum } from "@/api/services/global/enum";
import { IconActionStar } from "@/assets/icons";
import { InnerBox } from "@/components/elements/InnerBox";
import { YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { convertNumberToZipCode } from "@/utils/input";
import { findLabelByValue } from "@/utils/object";
import { formatPhone, truncateWithEllipsis } from "@/utils/string";
import { Stack, Box, Typography, Grid } from "@mui/material";
import dayjs from "dayjs";

type TTalentBasicInfoProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentBasicInfo: FCC<TTalentBasicInfoProps> = ({ defaultValues }) => {
  const { data: genderOptions } = useEnum("EGender");

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            基本情報
          </Typography>
        </Box>

        {defaultValues?.isRecommendedTalent && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconActionStar />
            <Typography>おすすめ人材</Typography>
          </Stack>
        )}
      </Stack>

      <Stack mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="氏名" value={defaultValues?.fullName ?? EMPTY_STR.TEXT} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem
              label="生年月日"
              value={
                defaultValues?.birthday && dayjs(defaultValues.birthday).isValid()
                  ? `${dayjs(defaultValues.birthday).format(YEAR_MONTH_DAY_STR_FORMAT)} (${dayjs().diff(dayjs(defaultValues.birthday), "year")}歳)`
                  : ""
              }
            />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="呼び名" value={defaultValues?.nickName ?? EMPTY_STR.TEXT} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="人材管理番号" value={defaultValues?.talentNumber ?? EMPTY_STR.TEXT} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="国籍" value={defaultValues?.nationality?.nationalityName ?? ""} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="性別" value={findLabelByValue(genderOptions, defaultValues?.gender)} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="電話番号" value={defaultValues?.phoneNumber ? formatPhone(defaultValues.phoneNumber) : null} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="メール" value={defaultValues?.email ? truncateWithEllipsis(defaultValues?.email, 40) : EMPTY_STR.TEXT} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TalentInfoItem label="居住地" value={findLabelByValue(TALENT_LOCATION_OPTIONS, defaultValues?.location)} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="宗教" value={defaultValues?.religion} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TalentInfoItem
              label="現住所"
              value={
                defaultValues?.zipCode || defaultValues?.prefecture || defaultValues?.city || defaultValues?.address
                  ? `${defaultValues.zipCode ? convertNumberToZipCode(defaultValues.zipCode) : ""} ${defaultValues.prefecture ?? ""}${defaultValues.city ?? ""}${defaultValues.address ?? ""}`.trim()
                  : EMPTY_STR.TEXT
              }
            />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TalentInfoItem label="本国における居住地" value={defaultValues?.country ?? EMPTY_STR.TEXT} isShowDivider={false} />
          </Grid>
        </Grid>
      </Stack>
    </InnerBox>
  );
};
